import React, { useState } from 'react';
import { Delete, Edit } from '@mui/icons-material';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Snackbar, Alert } from '@mui/material';
import { apiService } from '../../../../core/services/apiService';
import styles from '../SchedulerSetting.module.scss';
import { usePermissionCheck } from 'core/hooks/usePermissionCheck';

interface BroadcastGroup {
	id: number;
	groupName: string;
	participants?: any[];
	securityRoles?: any[];
	workplace?: string | null;
	isEntireWorkplace?: boolean;
	isEntireOrganization?: boolean;
}

interface BroadcastGroupsListProps {
	broadcastGroups: BroadcastGroup[];
	onBroadcastGroupCreated: () => void;
	onEditClick: (group: BroadcastGroup) => void;
}

const BroadcastGroupsList: React.FC<BroadcastGroupsListProps> = ({ 
	broadcastGroups, 
	onBroadcastGroupCreated, 
	onEditClick 
}) => {
	const { hasViewPermission, hasEditPermission, hasDeletePermission } = usePermissionCheck();
	const canViewBroadcast = hasViewPermission('Broadcast');
	const canEditBroadcast = hasEditPermission('Broadcast');
	const canDeleteBroadcast = hasDeletePermission('Broadcast');

	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [groupToDelete, setGroupToDelete] = useState<BroadcastGroup | null>(null);
	const [snackbar, setSnackbar] = useState<{
		open: boolean;
		message: string;
		severity: 'success' | 'error' | 'info' | 'warning';
	}>({
		open: false,
		message: '',
		severity: 'success',
	});

	const handleDeleteClick = (group: BroadcastGroup) => {
		if (!canDeleteBroadcast) {
			return;
		}
		setGroupToDelete(group);
		setDeleteDialogOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (!groupToDelete) return;
		
		try {
			await apiService.delete(`/broadcast_groups/${groupToDelete.id}`);
			setDeleteDialogOpen(false);
			setGroupToDelete(null);
			setSnackbar({
				open: true,
				message: 'Broadcast group deleted successfully',
				severity: 'success',
			});
			onBroadcastGroupCreated();
		} catch (e: any) {
			console.error('Failed to delete broadcast group:', e);
			const errorMessage = e?.message || 'Failed to delete broadcast group. Please try again.';
			setSnackbar({
				open: true,
				message: errorMessage,
				severity: 'error',
			});
			setDeleteDialogOpen(false);
			setGroupToDelete(null);
		}
	};

	const handleCancelDelete = () => {
		setDeleteDialogOpen(false);
		setGroupToDelete(null);
	};

	return (
		<>
			<div className={styles.tableContainer}>
				{!canViewBroadcast ? (
					<div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
						You do not have permissions to view Broadcast groups.
					</div>
				) : (
				<table className={styles.workplaceTable}>
					<thead>
						<tr>
							<th>Group Name</th>
							<th style={{ width: 96, textAlign: 'center' }}>Action</th>
						</tr>
					</thead>
					<tbody>
						{broadcastGroups.length === 0 ? (
							<tr>
								<td colSpan={2} style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
									No records found
								</td>
							</tr>
						) : (
							broadcastGroups.map((group) => {
								const isSystemGroup = group.isEntireWorkplace || group.isEntireOrganization;
								const editDisabled = isSystemGroup || !canEditBroadcast;
								const deleteDisabled = isSystemGroup || !canDeleteBroadcast;
								return (
									<tr key={group.id}>
										<td>
											<span>{group.groupName}</span>
										</td>
										<td style={{ textAlign: 'center', padding: '8px', width: 96 }}>
											<button
												className={styles.iconButton}
												title={
													editDisabled
														? isSystemGroup
															? "Cannot edit system groups"
															: "You do not have permission to edit"
														: "Edit"
												}
												onClick={() => !editDisabled && onEditClick(group)}
												disabled={editDisabled}
												style={{ 
													marginRight: '8px',
													opacity: editDisabled ? 0.5 : 1,
													cursor: editDisabled ? 'not-allowed' : 'pointer'
												}}
											>
												<Edit className={styles.editIcon} />
											</button>
											<button
												onClick={() => !deleteDisabled && handleDeleteClick(group)}
												title={
													deleteDisabled
														? isSystemGroup
															? "Cannot delete system groups"
															: "You do not have permission to delete"
														: "Delete Group"
												}
												className={styles.iconButton}
												disabled={deleteDisabled}
												style={{
													opacity: deleteDisabled ? 0.5 : 1,
													cursor: deleteDisabled ? 'not-allowed' : 'pointer'
												}}
											>
												<Delete className={styles.deleteIcon} />
											</button>
										</td>
									</tr>
								);
							})
						)}
					</tbody>
				</table>
				)}
			</div>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={deleteDialogOpen}
				onClose={handleCancelDelete}
				aria-labelledby="delete-dialog-title"
				aria-describedby="delete-dialog-description"
				maxWidth="sm"
				fullWidth
				style={{ zIndex: 9999 }}
				PaperProps={{
					style: {
						backgroundColor: 'white',
						boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
						borderRadius: '8px',
						minWidth: '400px'
					}
				}}
			>
				<DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
				<DialogContent>
					<DialogContentText id="delete-dialog-description">
						Are you sure you want to delete the broadcast group{' '}
						{groupToDelete && <strong>&ldquo;{groupToDelete.groupName}&rdquo;</strong>}? This action cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleCancelDelete}
						style={{ color: 'black' }}
					>
						Cancel
					</Button>
					<Button
						style={{ backgroundColor: 'red', color: 'white' }}
						onClick={handleConfirmDelete}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			{/* Snackbar for success/error messages */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					severity={snackbar.severity}
					variant="filled"
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</>
	);
};

export default BroadcastGroupsList;

